import * as actions from '@app/actions/index';
import {
	AccountLevelEnum,
	AccountStatusEnum,
	AccountSubscriptionEnum,
	CurrencyEnum,
	EnableDisableEnum,
	EventTypeEnum,
	PaymentOptions,
	SCACEnum,
	TransactionTypeEnum,
	UserRole,
	USPSTierSourceEnum,
} from '@app/app.enum';
import * as defaultUserImage from '@app/assets/images/default-user.jpg';
import { defaultCountry } from '@app/constants/core.constants';
import { IAxiosCancellation, IBaseEntity, ILocationProps } from '@app/models/core.models';
import * as models from '@app/models/index';
import * as React from 'react';
import { InjectedIntl } from 'react-intl';
import { RouteComponentProps } from 'react-router';

export interface IAuthState extends models.ICustomSnackBarProps {
	isAuthenticated: boolean;
	loading?: boolean;
	showForgotPasswordDialog?: boolean;
	isForgotPasswordLoading?: boolean;
	user: IUser;
	partnerAppId: string;
	email: string;
	parentAccountId: string;
	accountLevel: AccountLevelEnum;
	accountId: string;
	isTermsAccepted: boolean;
	showAPIKeyTextBox: boolean;
	signupKey: string;
	isSignUpKeyInQueryParam: boolean;
}

export interface ISignInComponentProps extends IAxiosCancellation, models.ISnackBarFeature {
	onUserSignIn: (state: models.IAppReduxAuthenticationState) => actions.IUserSignedInAction;
	intl: InjectedIntl;
	location?: ILocationProps;
}

export interface ISignUpFormFields {
	companyName: string;
	firstName: string;
	lastName: string;
	email: string;
	password: string;
	signupKey?: string;
	reCaptchaResponse: string;
}

export interface ISignUpComponentProps extends IAxiosCancellation, models.ISnackBarFeature {
	onUserSignUp: (state: models.IAppReduxAuthenticationState) => actions.IUserSignedUpAction;
	intl: InjectedIntl;
	location?: ILocationProps;
}

export interface IQuickSignUpConfigData {
	disableForm: boolean;
	disabledFormAction: () => void;
	prefillFormValues: ISignUpFormFields;
}
export interface IUserSignInRequest {
	userName: string;
	password: string;
}

export interface IUserSignInResponse {
	user: IUser;
	accessToken: string;
	refreshToken: string;
	account: IAccount;
	apiKey: string;
	visibleAccountNumber: string;
	status: AccountStatusEnum;
	apps: models.IAccountAppResponseVM[];
}

export interface ICustomerAccountDetails {
	account: IAccount;
	user: IUser[];
	visibleAccountNumber: string;
	partnerVisibleAccountNum: string;
	partnerUSPSTier: models.IUSPSTierConfig;
	balance: number;
	status: AccountStatusEnum;
	apps: models.IAccountAppResponseVM[];
	shipmentCount: number;
}

export interface ICustomerAccountDetailsState {
	account: IAccount;
	user: IUser[];
	visibleAccountNumber: string;
	partnerVisibleAccountNum: string;
	partnerUSPSTier: models.IUSPSTierConfig;
	status: AccountStatusEnum;
	apps: models.IAccountAppResponseVM[];
	uspsTierConfigs: models.IUSPSTierConfig[];
	uspsTierConfig: Record<'selected' | 'current', models.IUSPSTierConfig>;
	rateSets: models.IRateSet[];
	uniqueRateSetCarrier: string[]
	buttonLoading: boolean;
	balance: number;
	shipmentCount: number;
	users: models.IAdminAccountUserTreeTableData[];
	uspsConfigdataLoading: boolean;
	rateSetdataLoading: boolean;
	tierConfigDialogOpen: boolean;
}

export interface IUserSignUpRequest {
	email: string;
	password: string;
	companyName: string;
	firstName: string;
	lastName: string;
	signupKey: string;
	reCaptchaResponse: string;
}
export interface IUserRefreshTokenRequest {
	accessToken: string;
	refreshToken: string;
}

export interface IAccountBalance {
	autoBuyAmount: number;
	autoBuyThreshold: number;
	isAutoBuy: boolean;
	balance: number;
	minLimit?: number;
	maxLimit?: number;
	maxBalance?: number;
	achPaymentAccountResponse?: IACHPaymentAccountResponse;
	creditCardPaymentAccountResponse?: ICreditCardPaymentAccountResponse;
}

export interface IACHPaymentAccountResponse extends IPaymentAddress {
	accountHolderName?: string;
	active?: boolean;
	default?: boolean;
	lastFour?: string;
	routingNumber?: string;
	accountNumber?: string;
}

export interface ICreditCardPaymentAccountResponse extends IPaymentAddress {
	cardNumber?: string;
	cardHolderName?: string;
	active?: boolean;
	default?: boolean;
	lastFour?: string;
	expirationMonth?: string;
	expirationYear?: string;
}

export interface IPaymentMethod {
	achPaymentAccountResponse?: IACHPaymentAccountResponse;
	creditCardPaymentAccountResponse?: ICreditCardPaymentAccountResponse;
}

export interface IPaymentAddress {
	country?: string;
	address1?: string;
	address2?: string;
	city?: string;
	state?: string;
	postalCode?: string;
}

export interface IAccount extends models.IBaseEntity, models.IAccountBalance {
	name: string;
	product: string;
	currency: string;
	setupComplete: number;
	logo: string;
	users: IUser[];
	parentAccountId: string;
	childCount: number;
	level: AccountLevelEnum;
	email?: string;
	visibleAccountNumber: string;
	uspsTierConfig: models.IUSPSTierConfig;
	rateSet: models.IRateSet;
	subscriptionPlan: AccountSubscriptionEnum;
	subscriptionPlanChangeDate: Date;
	status: AccountStatusEnum;
	uspsTierConfigReturnsId: string;
	uspsTierConfigReturns: models.IUSPSTierConfig;
	USPSeVSReturnsMID: string;
	USPSeVSMID?: string;
	uspsTierSourceID: USPSTierSourceEnum;
}

export interface ISenderInfoFields {
	company: string;
	street1: string;
	street2: string;
	street3: string;
	city: string;
	state: string;
	zip: string;
	country: string;
	phone: string;
	email: string;
	name: string;
	logo?: string;
	verify?: boolean;
	save?: boolean;
}

export interface IAddress {
	id: string;
	accountId: string;
	name: string;
	company: string;
	street1: string;
	street2: string;
	street3: string;
	city: string;
	state: string;
	zip: string;
	country: string;
	phone?: string;
	email?: string;
	verify?: string;
	source?: string;
	isResidential?: boolean;
	verificationStatus?: string;
	saveAddress?: boolean;
}

export interface IUser extends models.IBaseEntity {
	firstName: string;
	lastName: string;
	email: string;
	userName: string;
	role: UserRole;
	isEmailVerified: boolean;
	account: IAccount;
	lastLoginDate: Date;
	isActivated: boolean;
	passwordChangeDueDate: Date;
}

export interface IApproveTermsAndConditions {
	accountId: string;
	result?: string;
}

export interface IUserForgotPasswordRequest {
	email: string;
}

export const initialUser = (): IUser => ({
	firstName: '',
	lastName: '',
	email: '',
	userName: '',
	role: UserRole.User,
	isEmailVerified: false,
	account: initialAccount(),
	lastLoginDate: null,
	createdBy: '',
	createdDate: null,
	modifiedBy: '',
	id: '',
	isActive: true,
	modifiedDate: null,
	isActivated: false,
	passwordChangeDueDate: null,
});

export const initialAccount = (): IAccount => ({
	autoBuyAmount: 0,
	autoBuyThreshold: 0,
	balance: 0,
	childCount: 0,
	createdBy: '',
	createdDate: null,
	currency: CurrencyEnum.USD,
	id: '',
	modifiedBy: '',
	isActive: true,
	modifiedDate: null,
	users: [
		{
			firstName: '',
			lastName: '',
			email: '',
			userName: '',
			role: UserRole.User,
			isEmailVerified: false,
			account: null,
			lastLoginDate: null,
			createdBy: '',
			createdDate: null,
			modifiedBy: '',
			id: '',
			isActive: true,
			modifiedDate: null,
			isActivated: false,
			passwordChangeDueDate: null,
		},
	],
	isAutoBuy: true,
	logo: '',
	name: '',
	parentAccountId: '',
	product: '',
	setupComplete: 0,
	level: AccountLevelEnum.Partner,
	visibleAccountNumber: '',
	uspsTierConfig: null,
	rateSet: null,
	subscriptionPlan: AccountSubscriptionEnum.Basic,
	subscriptionPlanChangeDate: null,
	status: AccountStatusEnum.Active,
	uspsTierConfigReturns: null,
	uspsTierConfigReturnsId: '',
	USPSeVSReturnsMID: '',
	uspsTierSourceID: USPSTierSourceEnum.Partner,
});

export const initialAuthState = (isAuthenticated?: boolean): IAuthState => ({
	autoHideDuration: 2000,
	isAuthenticated: isAuthenticated || false,
	loading: false,
	showForgotPasswordDialog: false,
	isForgotPasswordLoading: false,
	user: null,
	email: '',
	parentAccountId: '',
	accountLevel: AccountLevelEnum.Partner,
	accountId: '',
	isTermsAccepted: false,
	partnerAppId: '',
	showAPIKeyTextBox: false,
	signupKey: '',
	isSignUpKeyInQueryParam: false,
});

export interface IPurchasePostageFields {
	amount: number;
}

export interface IPaymentOptions {
	name: string;
	accountNumber: string;
	routingNumber: string;
	country: string;
	address1: string;
	address2: string;
	city: string;
	state: string;
	postalCode: string;
	cardNumber: string;
	expirationDate: string;
	securityCode: string;
	phoneNumber: string;
}

export interface IPaymentValueSetUp {
	cardHolderName?: string;
	accountHolderName?: string;
	expirationMonth?: string;
	expirationYear?: string;
	routingNumber?: string;
	lastFourAccount?: string;
	lastFourCredit?: string;
}

export interface IAccountPostageProps extends IAxiosCancellation, models.ISnackBarFeature {
	intl: InjectedIntl;
	location?: ILocationProps;
}

export interface IAccountPostageState extends models.ICustomSnackBarProps {
	autoBuyloading: boolean;
	dialogDescription: string;
	dialogNoButton: string;
	dialogTitle: string;
	dialogYesButton: string;
	openDialog: boolean;
	postageloading: boolean;
	paymentOptionValue: PaymentOptions;
	expiryDate: Date;
	paymentMethodLoading: boolean;
	isUpdate: boolean;
	currentBillingCountry: string;
	countries: ICountry[];
	states: IState[];
	defaultPostageAmount: number;
	defaultPayment: PaymentOptions;
	dataLoading: boolean;
	dialogCheck: string;
}

export const initialAccountPostage = (): IAccountPostageState => ({
	autoBuyloading: false,
	paymentMethodLoading: false,
	dialogDescription: '',
	dialogNoButton: '',
	dialogTitle: '',
	dialogYesButton: '',
	openDialog: false,
	postageloading: false,
	paymentOptionValue: PaymentOptions.None,
	expiryDate: new Date(Date.now()),
	isUpdate: false,
	defaultPayment: PaymentOptions.None,
	states: [],
	countries: [],
	defaultPostageAmount: 0,
	dataLoading: true,
	dialogCheck: '',
	currentBillingCountry: defaultCountry,
});

export interface IUserBasicInfo {
	firstName: string;
	lastName: string;
	email: string;
	userName: string;
	visibleAccountNumber: string;
}

export interface ICountry {
	id: string;
	name: string;
	code: string;
}

export interface IState {
	code: string;
	name: string;
	displayName: string;
}

export interface IChangePassword {
	oldPassword: string;
	newPassword: string;
	confirmPassword: string;
}

export interface IPaymentDefault {
	[PaymentOptions.ACH]: boolean;
	[PaymentOptions.CreditCard]: boolean;
}
export interface IProfileProps extends IAxiosCancellation, models.ISnackBarFeature {
	onCloseAccount: () => actions.IUserCloseAccountAction;
	intl: InjectedIntl;
}
export interface IProfileState extends models.ICustomSnackBarProps {
	userInfoLoading: boolean;
	changePasswordLoading: boolean;
	updateCompanyAddressLoading: boolean;
	hideResendVerificationLink: boolean;
	isEmailVerified: boolean;
	logo: string;
	imageSrc: string | ArrayBuffer;
	isOpen: boolean;
	onImgUploading: boolean;
	openDialog: boolean;
	reason: string;
	isAccountClosed: boolean;
	states: IState[];
	closeAccountLoading: boolean;
	dataLoading: boolean;
	company: string;
	passwordChangeDueDate: Date;
}

export interface ICloseAccountComponentState {
	loading: boolean;
	reason: string;
}

export interface ICloseAccountComponentProps {
	intl: InjectedIntl;
	openDialog: boolean;
	dialogDescription?: string;
	onCloseAccount: (state: models.ICloseAccountRequest) => Promise<void>;
	closeDialog: (dialogPanel: string, isCancel: boolean) => void;
}

export const initialProfileState = (): IProfileState => ({
	autoHideDuration: 2000,
	changePasswordLoading: false,
	userInfoLoading: false,
	updateCompanyAddressLoading: false,
	hideResendVerificationLink: false,
	isEmailVerified: false,
	logo: defaultUserImage,
	imageSrc: '',
	isOpen: false,
	onImgUploading: false,
	openDialog: false,
	reason: '',
	isAccountClosed: false,
	states: [],
	closeAccountLoading: false,
	dataLoading: true,
	company: '',
	passwordChangeDueDate: null,
});

export interface ICloseAccountRequest {
	reason: string;
	accountId?: string;
}
export interface IResetPasswordRequest {
	email: string;
	code: string;
	password: string;
	confirmPassword: string;
	reCaptchaResponse: string;
}

export interface IResetPasswordProps extends IAxiosCancellation, models.ISnackBarFeature {
	intl: InjectedIntl;
	location?: ILocationProps;
}

export interface IResetPasswordState extends IResetPasswordRequest, models.ICustomSnackBarProps {
	loading?: boolean;
}
export const initialResetPassword = (): IResetPasswordState => ({
	code: '',
	email: '',
	loading: false,
	password: '',
	confirmPassword: '',
	reCaptchaResponse: '',
});
export interface IAccountApp {
	id: string;
	appName: string;
	appDescription: string;
	dateCreated: number;
	account: IAccount;
	companyName: string;
}

export interface IAccountAppCardProps extends IAxiosCancellation, models.ISnackBarFeature {
	classes: {
		card: string;
		textLabel: string;
		cardWidth: string;
		cardHeight: string;
	};
	app?: IAccountAppResponseVM;
	handleClick?: (e: React.MouseEvent, app: models.IAccountAppResponseVM) => void;
	intl: InjectedIntl;
	onNewAppAdd?: (apps: models.IAccountAppResponseVM) => void;
	onAppUpdate?: (apps: models.IAccountAppResponseVM) => void;
	onAppDeletion?: (appId: string) => void;
	onAppStatusUpdate?: (appId: string, status: EnableDisableEnum) => void;
	appFilterButton?: React.ReactNode;
	apiVersion: models.ISystemConfigAPIVersion;
	showNewAppCardAsButton?: boolean;
	totalNumberOfApps: number;
}

export interface IAccountAppCardState {
	manageAppModalOpen: boolean;
	app: IAccountAppResponseVM;
	isNewApp: boolean;
	isAccountClosed: boolean;
	newAppFormData: IAccountAppResponseVM;
	activeStep: number;
	showLoader: boolean;
	steps: string[];
	selectedCarrier: models.ICarrier;
	carrierDetails: models.ICarrierDetails;
	carrierConfig: models.ICarrierAccountConfiguration;
	selectedSCAC: SCACEnum;
	openAppStatusDialog: boolean;
	openDeleteDialog: boolean;
	dialogTitle: string;
	dialogDescription: string | React.ReactNode;
	dialogButtonLoading: boolean;
	isDeleteAppFailed: boolean;
}

export const initialAppCardState = (
	app: IAccountAppResponseVM,
	steps: string[],
	isAccountClosed: boolean,
	selectedCarrier: models.ICarrier
): IAccountAppCardState => ({
	manageAppModalOpen: false,
	app: app || initialApp(),
	newAppFormData: null,
	isNewApp: !app,
	activeStep: 0,
	selectedCarrier,
	showLoader: false,
	steps: [...steps],
	isAccountClosed,
	carrierDetails: { name: '', description: '', carrierDescription: '' },
	carrierConfig: null,
	selectedSCAC: null,
	openAppStatusDialog: false,
	openDeleteDialog: false,
	dialogTitle: '',
	dialogDescription: '',
	dialogButtonLoading: false,
	isDeleteAppFailed: false,
});

export const initialApp = (): IAccountAppResponseVM => ({
	id: '',
	accountId: '',
	appId: '',
	appName: '',
	appDescription: '',
	dateCreated: new Date(),
	apiKey: '',
	apiSecret: '',
	testApiKey: '',
	testApiSecret: '',
	partnerAccountId: '',
	partnerName: '',
	apiVersion: '',
	helpUrl: '',
	status: EnableDisableEnum.Enabled,
	refreshToken: null,
});

export interface IAddCustomer {
	signupUrl: string;
	email: string | string[];
}

export interface IAccountTransaction {
	id: string;
	accountId: string;
	transactionId: string;
	eventType: EventTypeEnum;
	transactionType: TransactionTypeEnum;
	transactionDate: Date;
	amount: number;
	balance: number;
	currency: CurrencyEnum;
	comments?: string;
}

export interface IAccountAppResponseVM {
	id?: string;
	accountId?: string;
	appId?: string;
	appName?: string;
	appDescription?: string;
	dateCreated?: Date;
	apiKey?: string;
	apiSecret?: string;
	testApiKey?: string;
	testApiSecret?: string;
	partnerAccountId?: string;
	partnerName?: string;
	apiVersion?: string;
	helpUrl?: string;
	status?: EnableDisableEnum;
	refreshToken?: string;
}

export interface IAccountAppSelectComponentState {
	activeStep: number;
	selectedAppId: string;
}

export interface IAccountAppSelectComponentProps {
	children?: React.ReactNode;
	onAppSelect: (app: models.IAccountAppResponseVM) => void;
	apps: models.IAccountAppResponseVM[];
}

export const initialAccountAppSelectComponentState = (selectedAppId: string): IAccountAppSelectComponentState => ({
	activeStep: 0,
	selectedAppId,
});

export interface IAccountInvitationResponseVM {
	parentCompanyName: string;
	parentCompanyLogo: string;
	parentUserName: string;
	partnerCompanyName: string;
	appName: string;
}

export interface IChildAccountResponseVM {
	accountId: string;
	parentAccountId: string;
	userId: string;
	companyName: string;
	email: string;
	userName: string;
	name: string;
	shipmentCount: number;
	shipmentAmount: number;
	joiningDate: Date;
	lastLoginDate: Date;
	status: AccountStatusEnum;
	childCount: number;
	role?: UserRole;
	visibleAccountNumber?: string;
	partnerVisibleAccountNum?: string;
	accountLevel: AccountLevelEnum;
	subscriptionPlan: AccountSubscriptionEnum;
}

export interface IApp extends IBaseEntity {
	account: IAccount;
	accountId: string;
	name: string;
	description: string;
	apiVersion: string;
	helpUrl: string;
}

export interface IAppRegenerateCredentials {
	apiKey: string;
	apiSecret: string;
}
export interface IAutoBuyFields {
	autoBuyAmount: number;
	isAutoBuy: boolean;
	autoBuyThreshold: number;
}

export interface IBillingBalance {
	balance: number;
}

export interface IUSPSMID {
	USPSMID: string;
}

export interface ISubscriptionPlan {
	plan: AccountSubscriptionEnum;
	callsAllowedPerDay: string;
	callsAllowedPerMinute: string;
}

export interface IProductDetailsState {
	subscriptionPlans: ISubscriptionPlan[];
	disableChangePlan: boolean;
	currentPlan: AccountSubscriptionEnum;
	selectedPlan: AccountSubscriptionEnum;
	loading: boolean;
	account: models.IAccount;
	openDialog: boolean;
}

export interface IProductDetailsProp extends models.ISnackBarFeature {
	account: models.IAccount;
	intl: InjectedIntl;
	isPartnerAccount: boolean;
}

export interface ISubscriptionPlanUpdate {
	accountId: string;
	plan: AccountSubscriptionEnum;
}

export interface IPaymentMethodComponentState {
	loading: boolean;
	paymentOption: PaymentOptions;
	billingCountry: string;
	defaultPayment: PaymentOptions;
	countries: ICountry[];
	states: IState[];
	addMethod: boolean;
}

export interface IPaymentMethodComponentProps extends IAxiosCancellation, models.ISnackBarFeature {
	paymentMethodData?: {
		isUpdate: boolean;
		onUpdateCb: (updateState: boolean, paymentMethod?: PaymentOptions) => void;
		prefilledData: IPaymentValueSetUp;
		defaultMethod: PaymentOptions;
		prefilledPaymentMethod?: PaymentOptions;
	};
	hideNoPaymentScreen?: boolean;
	isForQuickSignUp?: boolean;
	intl: InjectedIntl;
	formButtons?: (
		onSubmit: (authTokenForQuickSignUp: string, callback: () => Promise<void>) => void,
		onCancel: () => void,
		onUpdate: () => void
	) => JSX.Element;
}

export interface IPaymentOptions {
	name: string;
	accountNumber: string;
	routingNumber: string;
	country: string;
	address1: string;
	address2: string;
	city: string;
	state: string;
	postalCode: string;
	cardNumber: string;
	expirationDate: string;
	securityCode: string;
	phoneNumber: string;
}

export interface IPaymentValueSetUp {
	cardHolderName?: string;
	accountHolderName?: string;
	expirationMonth?: string;
	expirationYear?: string;
	routingNumber?: string;
	lastFourAccount?: string;
	lastFourCredit?: string;
}

export interface IQuickSignUpComponentState {
	isStepLinear: boolean;
	accessToken: string;
	refreshToken: string;
	app: models.IAccountAppResponseVM;
	activeStep: number;
	disableForm: boolean;
	signUpFormData: models.ISignUpFormFields;
	paymentDetails: models.IPaymentValueSetUp;
	paymentMethod: PaymentOptions;
	isPaymentApiInvoked: boolean;
}

export interface IQuickSignUpStepperComponentProps extends RouteComponentProps<any> {
	disableForm: boolean;
	disabledFormAction: () => void;
	signUpFormData: models.ISignUpFormFields;
	onSignUp: (user: models.IUserSignInResponse, formData?: models.ISignUpFormFields, error?: any) => void;
}

export interface IQuickSignUpStepperComponentState {
	signUpKey: string;
}

export interface IPaymentMethodStepperComponentProps {
	location?: ILocationProps;
	accessToken: string;
	onSuccess: (paymentMethod: PaymentOptions, paymentDetails: models.IPaymentValueSetUp) => void;
	onNext: () => void;
	paymentInfo: {
		['method']: PaymentOptions;
		['details']: models.IPaymentValueSetUp;
	};
}

export interface IPaymentMethodStepperComponentState {
	loading: boolean;
	isNewDetails: boolean;
}

export interface ICredentialsInfoStepperComponentProps extends models.ISnackBarFeature {
	location?: ILocationProps;
	accessToken: string;
	app: models.IAccountAppResponseVM;
	updateStepper: () => void;
}

export interface ICredentialsInfoStepperComponentState {
	app: models.IAccountAppResponseVM;
}

export interface IQuickSignUpStepper {
	stepCompleted?: boolean;
	label: string;
}

export interface IAccountDisableService {
	accountId: string;
	serviceID: string;
	carrierCode: string;
	message: string;
}

export interface IAccountDisableSpecialService {
	accountId: string;
	specialServiceID: string;
	carrierCode: string;
	message: string;
}

export interface IAccountDisableEnableServiceRequest {
	accountId: string;
	details: IAccountDisableEnableServiceDetail[];
}

export interface IAccountDisableEnableServiceDetail {
	carrierCode: string;
	serviceCode: string;
	status: EnableDisableEnum;
	message?: string;
}

export interface IAccountDisableEnableSpecialServiceRequest {
	accountId: string;
	details: IAccountDisableEnableSpecialServiceDetail[];
}

export interface IDisableSpeicalService {
	code: string;
    createdDate: string;
    name: string;
    id: string;
}

export interface IAccountDisableEnableSpecialServiceDetail {
	carrierCode: string;
	specialServiceCode: string;
	status: EnableDisableEnum;
	message?: string;
}



// WEBPACK FOOTER //
// ./src/models/account.models.ts